import * as Yup from "yup";

const regex = /^[A-Za-záàâãéèêíïóôõöúçñÁÀÂÃÉÈÍÏÓÔÕÖÚÇÑ\n",""."";" ]+$/;
const regexTextArea = /^[A-Za-zÀ-ÿ0-9\s.,;:!?()'"%\-–—\n]+$/;

// ---- Lista de instituições/plataformas de cursos livres proibidas
const forbiddenInstitutions = [
  "udemy",
  "alura",
  "fundação bradesco",
  "fundacao bradesco",
  "senai",
  "sebrae",
  "aprenda mais",
];

// Função auxiliar de validação reutilizável
const validateForbiddenInstitutions = (value?: string) => {
  if (!value) return true;
  const normalizedValue = value.toLowerCase();
  return !forbiddenInstitutions.some((forbidden) =>
    normalizedValue.includes(forbidden)
  );
};

export const stepTwoSchema = Yup.object().shape({
  trilha1: Yup.string().required("Selecione a primeira trilha"),
  trilha2: Yup
    .string()
    .required("Selecione a segunda trilha")
    .notOneOf([Yup.ref("trilha1")], "A segunda trilha deve ser diferente da primeira"),

  instituicao: Yup.string()
    .when("matriculado", {
      is: "T",
      then: Yup.string()
        .required("Preencha o campo com o nome da instituição")
        .min(2, "São necessários 2 caracteres, no mínimo"),
    })
    .required("A instituição é obrigatória")
    .max(100, "A instituição deve ter no máximo 100 caracteres")
    .test(
      "instituicao-empty",
      "A Instituição de ensino não pode conter apenas espaço",
      (value: any) => {
        if (!value) return true;
        return value.trim().length > 0;
      }
    )
    .test(
      "instituicao-forbidden",
      "Plataformas de cursos livres (Udemy, Alura, SENAI, Sebrae, etc.) não são aceitas como Instituição de Ensino Superior ou Técnico.",
      validateForbiddenInstitutions
    ),

  curso: Yup.string()
    .when("matriculado", {
      is: "T",
      then: Yup.string()
        .required("Preencha o campo com o nome do curso")
        .min(2, "São necessários 2 caracteres, no mínimo"),
    })
    .required("O curso é obrigatório")
    .max(50, "O curso deve ter no máximo 50 caracteres")
    .matches(regex, {
      message: "O Curso deve ter apenas letras e espaços",
      excludeEmptyString: true,
    })
    .test("curso-empty", "O Curso não pode conter apenas espaço", (value: any) => {
      if (!value) return true;
      return value.trim().length > 0;
    })
    .test(
      "curso-forbidden",
      "Cursos livres ou plataformas online (Udemy, Alura, SENAI, Sebrae, etc.) não são aceitos.",
      validateForbiddenInstitutions
    ),

  qtdSemestres: Yup.number()
    .typeError("Preencha o campo apenas com o NÚMERO total de semestres do seu curso")
    .transform((val, orig) => (orig === "" || orig === null ? undefined : Number(orig)))
    .integer("Use apenas números inteiros")
    .min(2, "O curso deve ter no mínimo 2 semestres")
    .max(10, "O curso deve ter no máximo 10 semestres")
    .required("Preencha o número total de semestres do seu curso"),

  semestreAtual: Yup.number()
    .typeError("Preencha o campo apenas com o NÚMERO do seu semestre atual")
    .transform((val, orig) => (orig === "" || orig === null ? undefined : Number(orig)))
    .integer("Use apenas números inteiros")
    .min(1, "Você deve estar no mínimo no primeiro semestre")
    .max(10, "O semestre atual deve ser no máximo 10")
    .required("Preencha o número do seu semestre atual")
    .test(
      "lt-total",
      "O semestre atual deve ser menor que o total de semestres do curso",
      function (value) {
        const { qtdSemestres } = this.parent;
        if (value == null || qtdSemestres == null) return true;
        return value < qtdSemestres;
      }
    ),

  tipoGraduacao: Yup.string().required("Selecione o tipo de graduação do seu curso"),
  turno: Yup.string().required("Selecione o turno da sua graduação"),
  ingles: Yup.string().required("Selecione o nível de conhecimento em Inglês"),
  espanhol: Yup.string().required("Selecione o nível de conhecimento em Espanhol"),
  comoSoube: Yup.string().required("Selecione como você soube sobre o Vem Ser"),

  comprovanteMatricula: Yup.mixed()
    .test("required", "O comprovante de matrícula é obrigatório", (value) => value && value.length > 0)
    .test("fileType", "O tipo de arquivo não é suportado. Só é possível enviar PDF.", (value) =>
      value && value.length && ["application/pdf"].includes(value[0].type)
    )
    .test("fileSize", "O tamanho do arquivo deve ser de no máximo 1MB", (value) =>
      value && value.length && value[0].size <= 1 * 1024 * 1024
    ),

  algoimportante: Yup.string()
    .required("Campo obrigatório")
    .min(10, "São necessários 10 caracteres, no mínimo")
    .max(500, "O campo deve ter no máximo 500 caracteres")
    .matches(regexTextArea, {
      message: "O campo aceita apenas letras, números, espaços e pontuação simples (.,;:!?()'\"%-–—).",
      excludeEmptyString: true,
    })
    .test(
      "algoimportante",
      "Este campo não pode conter apenas espaço",
      (value: any) => {
        if (!value) return true;
        return value.trim().length > 0;
      }
    ),

  lgpdBoolean: Yup.boolean().oneOf(
    [true],
    "É necessário aceitar os termos politíca de privacidade para prosseguir"
  ),

  polPrivacidade: Yup.boolean().oneOf(
    [true],
    "É necessário aceitar os termos de tratamento de dados e geolocalização para prosseguir"
  ),

  resposta: Yup.string()
    .required("Campo obrigatório")
    .min(10, "São necessários 10 caracteres, no mínimo")
    .max(500, "O campo deve ter no máximo 500 caracteres")
    .matches(regexTextArea, {
      message: "O campo aceita apenas letras, números, espaços e pontuação simples (.,;:!?()'\"%-–—).",
      excludeEmptyString: true,
    })
    .test(
      "resposta",
      "A resposta não pode conter apenas espaço",
      (value: any) => {
        if (!value) return true;
        return value.trim().length > 0;
      }
    ),

  github: Yup.string()
    .transform((v) => (v ?? "").trim())
    .max(255, "O link do GitHub deve ter no máximo 255 caracteres")
    .matches(
      /^$|^(https?:\/\/)?(www\.)?github\.com\/[A-Za-z0-9-]{1,255}\/?$/,
      "Informe o link do seu perfil do GitHub (ex.: github.com/seu-usuario ou https://github.com/seu-usuario)"
    ),

  linkedin: Yup.string()
    .transform((v) => (v ?? "").trim())
    .max(255, "O link do LinkedIn deve ter no máximo 255 caracteres")
    .matches(
      /^$|^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[A-Za-z0-9-_%]+\/?$/,
      "Informe o link do seu perfil do LinkedIn (ex.: linkedin.com/in/seu-usuario/ ou https://www.linkedin.com/in/seu-usuario/)"
    ),

  curriculo: Yup.mixed()
    .test("required", "O currículo é obrigatório", (value) => value && value.length > 0)
    .test("fileType", "O tipo de arquivo não é suportado. Só é possível enviar PDF.", (value) =>
      value && value.length && ["application/pdf"].includes(value[0].type)
    )
    .test("fileSize", "O tamanho do arquivo deve ser de no máximo 1MB", (value) =>
      value && value.length && value[0].size <= 1 * 1024 * 1024
    ),

  descConfigPc: Yup.string()
    .required("Preencha a descrição das configurações do seu computador.")
    .max(500, "O campo deve ter no máximo 500 caracteres")
    .matches(regexTextArea, {
      message: "O campo aceita apenas letras, números, espaços e pontuação simples (.,;:!?()'\"%-–—).",
      excludeEmptyString: true,
    }),
});